// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { createHandlerChainRunner, Handler } from "@insite/client-framework/HandlerCreator";
import siteMessage from "@insite/client-framework/SiteMessage";
import { ExcelHotSheetView } from "@stanion/App/Pages/HotSheetPage";

type HandlerType = Handler<
    { views: ExcelHotSheetView },
    {
        dataForUnparse: Array<{ title: string; headers: Array<any>; data?: Array<any> }>;
    }
>;

export const CreateDataForUnparse: HandlerType = props => {
    const headers = [siteMessage("Custom_HotSheet_Size"), siteMessage("Custom_HotSheet_Price")];

    const dataForUnparse = Object.entries(props.parameter.views).map(([key, view]) => {
        const mappedData = view?.products?.map(product => {
            return [product.erpNumber, product.price];
        });
        return {
            title: view.title,
            headers,
            data: mappedData,
        };
    });

    props.dataForUnparse = dataForUnparse;
};

export const GenerateCsv: HandlerType = async props => {
    const XLSX = await import(/* webpackChunkName: "xlsx" */ "xlsx");
    if (!props.dataForUnparse) {
        return;
    }

    const wb = XLSX.utils.book_new(); // Create a new workbook
    const fileName = `Hot_Sheet_${new Date().toISOString()}`;

    // Create a sheet with multiple tables and titles
    const wsData: Array<any> = []; // Array to store all the rows for the worksheet

    props.dataForUnparse.forEach(table => {
        // Add title row for the table
        wsData.push([table.title]); // Add the title as a single cell row

        if (!table.data) {
            return;
        }
        // Check if there's data to add headers and rows
        if (table.data.length > 0) {
            // Add table headers
            wsData.push(table.headers);

            // Add table data
            table.data.forEach(row => {
                wsData.push(row);
            });
        }

        // Add another empty row between tables (optional)
        wsData.push([]);
    });

    // Create a worksheet from the wsData
    const ws = XLSX.utils.aoa_to_sheet(wsData);

    // Append the worksheet to the workbook
    XLSX.utils.book_append_sheet(wb, ws, "Report");

    // Write the workbook and download it
    XLSX.writeFile(wb, `${fileName}.xlsx`);
};

export const chain = [CreateDataForUnparse, GenerateCsv];

const exportHotSheet = createHandlerChainRunner(chain, "ExportHotSheet");
export default exportHotSheet;
